import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { createSearchParams, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useAppSelector } from "../../Store"
import { useUser } from "../../Services"
import { setStates } from "../../Store/StateHandlers/active-slice"
import { hardNavigateToLogin, Pages, NavigationFunction as RootNavigation } from "../../Navigation/root"
import { StyleObject } from "styletron-react"
import { logos, frames, glyphs } from "../../Styles"
import { NewButton, NewSelect } from ".."
import { ButtonTypes, SelectFieldOption_Values, NewFieldStyles } from "../../Definitions"

interface LogoDisplay_Props {
  ChooseSite?: boolean
  goBackBtn?: string,
  overrides?: {
    gridArea?: string
    borderRadius?: string
  }
  selectedPage?: string
  focus_view?: boolean
}

const TIMEFRAME_MS = 4000; // 3 seconds window for 25 clicks
const getVersion = (): string => {
  return process.env.REACT_APP_VERSION ? `Version ${process.env.REACT_APP_VERSION}` : atob('SSBCbGFtZSBNVEg=')
}

export default function LogoDisplay({ goBackBtn, overrides, ChooseSite, selectedPage, focus_view }: LogoDisplay_Props) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { userFarms, getUserFarms, getFarmSettings, doLogOut } = useUser()
  const activeFarm = useAppSelector<string | undefined>(({ activeData }) => activeData?.farm)
  const navigator = useNavigate()

  const [, setClicks] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [showVersion, setShowVersion] = useState(false);

  const handleClick = useCallback(() => {
    const now = Date.now();
    setClicks((prevClicks) => {
      if (prevClicks === 0) {
        // Start new sequence
        setStartTime(now);
        return 1;
      }

      // Check if within timeframe
      if (now - startTime > TIMEFRAME_MS) {
        // Too slow—reset sequence
        setStartTime(now);
        return 1;
      }

      const nextClicks = prevClicks + 1;
      if (nextClicks >= 25) {
        setShowVersion(true);
        setTimeout(() => setShowVersion(false), 5000); // Hide after 5s
        return 0;
      }
      return nextClicks;
    });
  }, [startTime]);

  useEffect(() => {
    getUserFarms(undefined, !ChooseSite)
  }, [getUserFarms])

  useEffect(() => {
    if (activeFarm && localStorage.getItem('activedomain')) {
      getFarmSettings(activeFarm)
    }
    if (activeFarm && !localStorage.getItem("activedomain")) {
      localStorage.setItem("activedomain", activeFarm);
    }
  }, [activeFarm, getFarmSettings])

  const _Root = useMemo<StyleObject>(() => {
    return Object.assign(
      {} as StyleObject,
      overrides && overrides.gridArea ? { gridArea: overrides.gridArea } : {},
      overrides && overrides.borderRadius ? { borderRadius: overrides.borderRadius } : { borderRadius: "10px 0px 0px 0px" }
    )
  }, [overrides])

  const Data = useMemo<{ label: string; domain: string; icon: JSX.Element }[]>(() => {
    const focusOption =
      selectedPage !== "op_operations"
        ? {
          label: t("loginFlow.def.switchTofocus_view", { defaultValue: "Switch to Focus View" }),
          domain: "navigate-to-focus_view",
          icon: <glyphs.focus_view size={focus_view ? "25px" : "20px"} />,
        }
        : {
          label: t("loginFlow.def.switchToFullView", { defaultValue: "Switch to Full View" }),
          domain: "navigate-to-fullView",
          icon: <glyphs.FullView size={focus_view ? "25px" : "20px"} />,
        }

    const siteListOption = {
      label: t("loginFlow.def.goToSiteList", { defaultValue: "Go to Site List" }),
      domain: "navigate-to-list",
      icon: <glyphs.SiteList size={focus_view ? "25px" : "20px"} />,
    }

    const logoutOption = {
      label: t("loginFlow.login.logOut", { defaultValue: "Log Out" }),
      domain: "logout",
      icon: <glyphs.Logout size={focus_view ? "25px" : "20px"} />,
    }

    // If userFarms not loaded, just show site list + logout
    if (!userFarms) {
      return [focusOption, siteListOption, logoutOption]
    }

    // Map user farms to options
    const farmOptions = userFarms.map(farm => ({
      label: farm.name,
      domain: farm.domain,
      icon: <></>,
    }))

    // Order: focus → site list → logout → farms
    return [focusOption, siteListOption, logoutOption, ...farmOptions]
  }, [focus_view, selectedPage, t, userFarms])


  const handleChange = (selectedOption: SelectFieldOption_Values | null) => {
    if (selectedOption === null) {
      return;
    }

    if (selectedOption.domain === 'navigate-to-list') {
      // dispatch(setStates({ type: 'farm', farm: undefined }));
      // RootNavigation({ Navigator: navigator, navigation_page: Pages.login,  });
      hardNavigateToLogin(focus_view ? Pages.op_operations : undefined)
    } else if (selectedOption.domain === 'navigate-to-focus_view') {
      RootNavigation({ Navigator: navigator, navigation_page: Pages.op_operations, navigation_tab: undefined, replace: true, navArgs: { selected: selectedPage }, currentDomain: activeFarm });
    } else if (selectedOption.domain === 'navigate-to-fullView') {
      RootNavigation({ Navigator: navigator, navigation_page: Pages.kpi, navigation_tab: undefined, replace: true, navArgs: { selected: selectedPage }, currentDomain: activeFarm });
    } else if (selectedOption.domain === 'logout') {
      doLogOut();
      const tempLink = document.createElement('a');
      tempLink.href = window.location.origin;

      tempLink.rel = 'noreferrer';

      tempLink.click()
      // RootNavigation({ Navigator: navigator, navigation_page: Pages.def});
    } else {
      const selectedFarm = userFarms?.find((farm) => farm.domain === selectedOption.domain);
      if (selectedFarm && selectedFarm.legacy) {
        // RootNavigation({ Navigator: navigator, navigation_page: Pages.login, navigation_tab: undefined, replace: true, navArgs: { selected: selectedPage }});
        hardNavigateToLogin()
      }

      if (activeFarm !== selectedOption.domain) {
        dispatch(setStates({ type: 'farm', farm: selectedOption.domain }));
      }

      if (selectedFarm?.domain && activeFarm !== selectedOption.domain) {
        window.location.href = window.location.origin + window.location.pathname + "?" + createSearchParams({ domain: selectedFarm.domain }).toString();
      }
    }
  };

  return (
    <frames.LogoDisplay $focusView={focus_view} $style={_Root}>
      <img src={logos.freedaSolutions} style={{ width: "275px", marginBottom: "15px", cursor: "default" }} alt="Company logo" onClick={handleClick} />
      {showVersion && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            padding: "20px",
            background: "rgba(0,0,0,0.9)",
            color: "white",
            borderRadius: 8,
            textAlign: "center",
            boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            zIndex: "999999999999999999999999999999999999999999"
          }}
        >
          🥚 Easter Egg! 🥚
          <br />
          {getVersion()}
        </div>
      )}
      {/* Go Back Button on Logo Display */}
      {!goBackBtn ? (<></>) : (
        <NewButton
          content={goBackBtn}
          startEnhancer={glyphs.Arrow({ rotate: 180 })}
          buttonType={ButtonTypes.textButton}
          onClick={() => {
            // RootNavigation({ Navigator: navigator, navigation_page: Pages.login, currentDomain: activeFarm})
            hardNavigateToLogin()
          }}
        />
      )}
      {/* SelectField to choose site but also go to site list in loginflow */}
      {!ChooseSite ? (<></>) :
        <NewSelect
          isSearchable
          options={Data}
          onChange={(e) => handleChange(e ?? null)}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greyDarkSecondary,
            focus_view: focus_view ? true : false,
            menuWidth: focus_view ? "100%" : "280px",
            spliceBorder: 3
          }}
          valueOverrides={{
            defaultValue: Data.find(e => e.domain === activeFarm)
          }}
        />
      }
    </frames.LogoDisplay>
  )
}
